import { noop } from '@direct-frontend/stdlib';
import { createApi, createBaseQuery } from '@adv-frontend/modules-query';
import { invalidateOnboardingCache } from '#shared/api/onboarding';
import { apiV1Request } from '#shared/api/requests';
import {
    OpenApiConfirmCompanyInnMethod,
    type OpenApiConfirmCompanyInnResponseSuccess,
    OpenApiConfirmCompanyInnUrl,
    OpenApiGetCompanyAddressMethod,
    type OpenApiGetCompanyAddressResponseSuccess,
    OpenApiGetCompanyAddressUrl,
    OpenApiGetCompanyFaviconMethod,
    type OpenApiGetCompanyFaviconResponseSuccess,
    OpenApiGetCompanyFaviconUrl,
    OpenApiGetCompanyInnMethod,
    type OpenApiGetCompanyInnResponseSuccess,
    OpenApiGetCompanyInnUrl,
    OpenApiGetCompanyLogoMethod,
    type OpenApiGetCompanyLogoResponseSuccess,
    OpenApiGetCompanyLogoUrl,
    OpenApiGetCompanyNameMethod,
    type OpenApiGetCompanyNameResponseSuccess,
    OpenApiGetCompanyNameUrl,
    OpenApiGetCompanyPhonesMethod,
    type OpenApiGetCompanyPhonesResponseSuccess,
    OpenApiGetCompanyPhonesUrl,
    OpenApiSetCompanyAddressMethod,
    type OpenApiSetCompanyAddressRequest,
    OpenApiSetCompanyAddressUrl,
    OpenApiSetCompanyFaviconMethod,
    type OpenApiSetCompanyFaviconRequest,
    OpenApiSetCompanyFaviconUrl,
    OpenApiSetCompanyInnMethod,
    type OpenApiSetCompanyInnRequest,
    type OpenApiSetCompanyInnResponseSuccess,
    OpenApiSetCompanyInnUrl,
    OpenApiSetCompanyLogoMethod,
    type OpenApiSetCompanyLogoRequest,
    OpenApiSetCompanyLogoUrl,
    OpenApiSetCompanyNameMethod,
    type OpenApiSetCompanyNameRequest,
    OpenApiSetCompanyNameUrl,
    OpenApiSetCompanyPhonesMethod,
    type OpenApiSetCompanyPhonesRequest,
    OpenApiSetCompanyPhonesUrl,
} from '#shared/api/schema';

export const api = createApi({
    name: 'entities/company/api/api',
    baseQuery: createBaseQuery(apiV1Request),
    endpoints: (builder) => {
        return {
            getCompanyAddress: builder.query<OpenApiGetCompanyAddressResponseSuccess, void>({
                query: () => {
                    return {
                        method: OpenApiGetCompanyAddressMethod,
                        url: OpenApiGetCompanyAddressUrl,
                    };
                },
            }),
            setCompanyAddress: builder.mutation<void, OpenApiSetCompanyAddressRequest>({
                query: (payload) => {
                    return {
                        method: OpenApiSetCompanyAddressMethod,
                        url: OpenApiSetCompanyAddressUrl,
                        body: payload,
                    };
                },
                onQueryStarted: async (arg, { queryFulfilled }) => {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    api.endpoints.getCompanyAddress.upsertQueryData(undefined, arg);
                    invalidateOnboardingCache();
                },
            }),

            getCompanyFavicon: builder.query<OpenApiGetCompanyFaviconResponseSuccess, void>({
                query: () => {
                    return {
                        method: OpenApiGetCompanyFaviconMethod,
                        url: OpenApiGetCompanyFaviconUrl,
                    };
                },
            }),
            setCompanyFavicon: builder.mutation<void, OpenApiSetCompanyFaviconRequest>({
                query: (payload) => {
                    return {
                        method: OpenApiSetCompanyFaviconMethod,
                        url: OpenApiSetCompanyFaviconUrl,
                        body: payload,
                    };
                },
                onQueryStarted: async (arg, { queryFulfilled }) => {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    api.endpoints.getCompanyFavicon.upsertQueryData(undefined, arg);
                    invalidateOnboardingCache();
                },
            }),

            getCompanyInn: builder.query<OpenApiGetCompanyInnResponseSuccess, void>({
                query: () => {
                    return {
                        url: OpenApiGetCompanyInnUrl,
                        method: OpenApiGetCompanyInnMethod,
                    };
                },
            }),
            setCompanyInn: builder.mutation<OpenApiSetCompanyInnResponseSuccess, OpenApiSetCompanyInnRequest>({
                query: (payload) => {
                    return {
                        url: OpenApiSetCompanyInnUrl,
                        method: OpenApiSetCompanyInnMethod,
                        body: payload,
                    };
                },
                onQueryStarted: async (_, { queryFulfilled }) => {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    api.endpoints.getCompanyInn.upsertQueryData(undefined, queryFulfilledResult.data);
                    invalidateOnboardingCache();
                },
            }),
            confirmCompanyInn: builder.mutation<OpenApiConfirmCompanyInnResponseSuccess, void>({
                query: () => {
                    return {
                        url: OpenApiConfirmCompanyInnUrl,
                        method: OpenApiConfirmCompanyInnMethod,
                    };
                },
                onQueryStarted: async (_, { queryFulfilled }) => {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    api.endpoints.getCompanyInn.updateQueryData(undefined, (draft) => {
                        draft.inn_confirmed = true;
                    });
                    invalidateOnboardingCache();
                },
            }),

            getCompanyLogo: builder.query<OpenApiGetCompanyLogoResponseSuccess, void>({
                query: () => {
                    return {
                        method: OpenApiGetCompanyLogoMethod,
                        url: OpenApiGetCompanyLogoUrl,
                    };
                },
            }),
            setCompanyLogo: builder.mutation<void, OpenApiSetCompanyLogoRequest>({
                query: (payload) => {
                    return {
                        method: OpenApiSetCompanyLogoMethod,
                        url: OpenApiSetCompanyLogoUrl,
                        body: payload,
                    };
                },
                onQueryStarted: async (arg, { queryFulfilled }) => {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    api.endpoints.getCompanyLogo.upsertQueryData(undefined, arg);
                    invalidateOnboardingCache();
                },
            }),

            getCompanyName: builder.query<OpenApiGetCompanyNameResponseSuccess, void>({
                query: () => {
                    return {
                        method: OpenApiGetCompanyNameMethod,
                        url: OpenApiGetCompanyNameUrl,
                    };
                },
            }),
            setCompanyName: builder.mutation<void, OpenApiSetCompanyNameRequest>({
                query: (payload) => {
                    return {
                        method: OpenApiSetCompanyNameMethod,
                        url: OpenApiSetCompanyNameUrl,
                        body: payload,
                    };
                },
                onQueryStarted: async (_, { queryFulfilled }) => {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    invalidateOnboardingCache();
                },
            }),

            getCompanyPhones: builder.query<OpenApiGetCompanyPhonesResponseSuccess, void>({
                query: () => {
                    return {
                        method: OpenApiGetCompanyPhonesMethod,
                        url: OpenApiGetCompanyPhonesUrl,
                    };
                },
            }),
            setCompanyPhones: builder.mutation<void, OpenApiSetCompanyPhonesRequest>({
                query: (payload) => {
                    return {
                        method: OpenApiSetCompanyPhonesMethod,
                        url: OpenApiSetCompanyPhonesUrl,
                        body: payload,
                    };
                },
                onQueryStarted: async (arg, { queryFulfilled }) => {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    api.endpoints.getCompanyPhones.upsertQueryData(undefined, arg);
                    invalidateOnboardingCache();
                },
            }),
        };
    },
});

export const useGetCompanyAddress = api.useGetCompanyAddressQuery;
export const useSetCompanyAddress = api.useSetCompanyAddressMutation;

export const useGetCompanyFavicon = api.useGetCompanyFaviconQuery;
export const setCompanyFavicon = api.endpoints.setCompanyFavicon.mutate;

export const useGetCompanyInn = api.useGetCompanyInnQuery;
export const useLazyGetCompanyInn = api.useLazyGetCompanyInnQuery;
export const setCompanyInn = api.endpoints.setCompanyInn.mutate;
export const confirmCompanyInn = api.endpoints.confirmCompanyInn.mutate;

export const useGetCompanyLogo = api.useGetCompanyLogoQuery;
export const getCompanyLogo = api.endpoints.getCompanyLogo.query;
export const useSetCompanyLogo = api.useSetCompanyLogoMutation;

export const useGetCompanyName = api.useGetCompanyNameQuery;
export const getCompanyName = api.endpoints.getCompanyName.query;
export const setCompanyName = api.endpoints.setCompanyName.mutate;

export const useGetCompanyPhones = api.useGetCompanyPhonesQuery;
export const useSetCompanyPhones = api.useSetCompanyPhonesMutation;
